import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import { type UserProfile, fetchProfile, logout as apiLogout, login as apiLogin } from '../api/auth'

/**
 * BFF auth (WEB-408): the browser holds only an httpOnly session cookie set by
 * the backend OIDC callback. There is no access/refresh token in JS, no PKCE,
 * and no token refresh timer — the backend owns the tokens and refreshes them
 * server-side. The profile is loaded from /auth/me using the cookie.
 */
interface AuthState {
  user: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (returnTo?: string) => void
  logout: () => Promise<void>
  /** Resolves to true when a user profile was loaded, false otherwise. */
  refreshProfile: () => Promise<boolean>
}

const AuthContext = createContext<AuthState>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: async () => {},
  refreshProfile: async () => false,
})

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Returns true only when a profile was actually loaded. Callers rely on this:
  // an unauthenticated /auth/me returns 401, which must surface as "not loaded"
  // rather than a silent success. Reporting a failed load as a "loaded" user is
  // what produced the WEB-406 infinite login loop.
  const loadProfile = useCallback(async (): Promise<boolean> => {
    try {
      const profile = await fetchProfile()
      setUser(profile)
      return true
    } catch {
      setUser(null)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const login = useCallback((returnTo?: string) => {
    apiLogin(returnTo)
  }, [])

  const logout = useCallback(async () => {
    await apiLogout()
    setUser(null)
  }, [])

  const refreshProfile = useCallback(async (): Promise<boolean> => {
    return loadProfile()
  }, [loadProfile])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

function messageForAuthError(code: string): string {
  switch (code) {
    case 'account_deleted':
      return 'Dit account is verwijderd. Neem contact op met support@globalcontrolgroup.com om het te herstellen.'
    default:
      return 'Inloggen is niet gelukt. Probeer het opnieuw of neem contact op met support.'
  }
}

/**
 * Handles the /auth/callback page. Under the BFF the backend performs the OIDC
 * code exchange and redirects straight to the return path on success, so this
 * page is normally reached only on FAILURE (backend redirects here with
 * ?auth_error). It never triggers a new login — that was the WEB-406 loop.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const { refreshProfile } = useAuth()
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current) return
    handled.current = true

    const params = new URLSearchParams(window.location.search)
    const authError = params.get('auth_error')

    // Clear the query so a refresh can't replay it.
    if (authError) {
      window.history.replaceState({}, '', window.location.pathname)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(messageForAuthError(authError))
      setStatus('error')
      return
    }

    // No error flag: confirm a session actually exists (defensive — the happy
    // path lands on the return page, not here).
    refreshProfile().then((loaded) => {
      if (loaded) {
        setStatus('success')
        return
      }
      setError(messageForAuthError('auth_failed'))
      setStatus('error')
    })
  }, [refreshProfile])

  return { status, error }
}
